import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import http from "../../utils/axios";
import { useDispatch, useSelector } from "react-redux";
import { addToCart, increaseQuantity, decreaseQuantity, removeFromCart } from "../../store/cartSlice";
import StarRating from "../../components/StarRating";

const ProductDetailPage = () => {
    const { id } = useParams();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [quantity, setQuantity] = useState(1);

    const isAuth = useSelector((state) => state.user.isAuth);
    const cartItems = useSelector((state) => state.cart.items);
    const existingItem = cartItems.find(item => item.productId === id);

    const handleAddToCart = () => {
        if (!isAuth) {
            navigate("/login");
        }
        if (product.stock > 0) {
            dispatch(addToCart({
                productId: product._id,
                cover: product.cover,
                title: product.title,
                price: product.price,
                quantity: quantity,
            }));
            setQuantity(1); // Reset quantity after adding to cart
        } else {
            alert("Product is out of stock");
        }
    };

    const handleIncrease = () => {
        if (existingItem && existingItem.quantity < product.stock) {
            dispatch(increaseQuantity({ productId: existingItem.productId }));
            setQuantity(prev => prev + 1);
        }
    };

    const handleDecrease = () => {
        if (existingItem) {
            if (existingItem.quantity > 1) {
                dispatch(decreaseQuantity({ productId: existingItem.productId }));
                setQuantity(prev => prev - 1);
            } else {
                // Remove the item from the cart if quantity reaches 0
                dispatch(removeFromCart({ productId: existingItem.productId }));
                setQuantity(1); // Reset quantity to 1 for future additions
            }
        }
    };

    useEffect(() => {
        const fetchProduct = async () => {
            try {
                const { data } = await http.get(`/api/products/${id}`);
                setProduct(data);
            } catch (error) {
                console.error("Error fetching product details:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchProduct();
    }, [id]);

    if (loading) return <p>Loading...</p>;
    if (!product) return <p>Product not found</p>;

    const nextImage = () => {
        setCurrentImageIndex((prevIndex) => (prevIndex + 1) % product.images.length);
    };

    const prevImage = () => {
        setCurrentImageIndex((prevIndex) => (prevIndex - 1 + product.images.length) % product.images.length);
    };

    const selectImage = (index) => {
        setCurrentImageIndex(index);
    };

    return (
        <div className="container mx-auto p-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Product Images */}
                <div>
                    <div className="relative">
                        <img
                            src={product.images[currentImageIndex]}
                            alt={product.title}
                            className="w-full h-auto object-cover rounded-lg shadow-md"
                        />
                        {/* Previous Button */}
                        <button
                            onClick={prevImage}
                            className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white rounded-full p-2 shadow"
                        >
                            &lt;
                        </button>
                        {/* Next Button */}
                        <button
                            onClick={nextImage}
                            className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white rounded-full p-2 shadow"
                        >
                            &gt;
                        </button>
                    </div>
                    <div className="flex mt-4 gap-2">
                        {product.images.map((image, index) => (
                            <img
                                key={index}
                                src={image}
                                alt={`Thumbnail ${index}`}
                                className={`w-20 h-20 object-cover rounded-lg border cursor-pointer ${currentImageIndex === index ? "border-blue-500" : "border-gray-300"}`}
                                onClick={() => selectImage(index)}
                            />
                        ))}
                    </div>
                </div>

                {/* Product Details */}
                <div className="space-y-4">
                    <h1 className="text-3xl font-bold">{product.title}</h1>
                    <StarRating rating={product.reviewAverage} reviews={product.reviewCount || 0} showCount={true} />
                    <p className="text-gray-600">{product.description}</p>
                    <p className="text-2xl font-semibold text-red-600">${product.price.toFixed(2)}</p>
                    <p className="text-sm text-gray-500">
                        Stock:{" "}
                        <span className={`font-medium ${product.stock > 0 ? "text-green-600" : "text-red-600"}`}>
                            {product.stock > 0 ? `${product.stock} available` : "Out of stock"}
                        </span>
                    </p>

                    <div className="flex items-center space-x-4 mt-4">
                        {existingItem ? (
                            <div className="flex items-center">
                                <button onClick={handleDecrease} className="px-2 py-1 bg-gray-200 rounded hover:bg-gray-300">-</button>
                                <span className="px-4">{existingItem.quantity}</span>
                                <button onClick={handleIncrease} className="px-2 py-1 bg-gray-200 rounded hover:bg-gray-300">+</button>
                            </div>
                        ) : (
                            <button
                                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                                onClick={handleAddToCart}
                            >
                                Add to Cart
                            </button>
                        )}
                        <button
                            className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                            onClick={() => window.history.back()}
                        >
                            Go Back
                        </button>
                    </div>
                </div>
            </div>

            {/* Reviews Section */}
            <div className="mt-8">
                <h2 className="text-2xl font-bold">Customer Reviews</h2>
                {product.reviews.length > 0 ? (
                    <div className="mt-4 space-y-4">
                        {product.reviews.map((review) => (
                            <div key={review.id} className="border p-4 rounded-lg shadow">
                                <StarRating rating={review.rating} reviews={1} />
                                <h3 className="font-semibold">{review.title}</h3>
                                <p className="text-gray-600">{review.comment}</p>
                                <p className="text-sm text-gray-500">
                                    Reviewed by&nbsp;
                                    <span className="text-purple-700">{review.reviewer[0].name}</span>
                                    &nbsp;on {new Date(review.submissionTime).toLocaleDateString()}
                                </p>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p>No reviews yet.</p>
                )}
            </div>
        </div>
    );
};

export default ProductDetailPage;

